import { Injectable } from '@angular/core';
import { DialogNgbService } from '@motivforce/mx-library-angular';
import { IsLoadingService } from '@service-work/is-loading';
import { BehaviorSubject } from 'rxjs';

import { ActivityRestService } from '../api/rest/activity-rest.service';
import { UploadRestService } from '../api/rest/upload-rest.service';
import { Activity, ActivityRequirement } from '../model/core/activity';
import { SubmitProof } from '../model/core/submit-proof';
import { SubmissionAnswer } from '../model/submission-answer';

@Injectable({
  providedIn: 'root',
})
export class ActivityStoreService {
  private readonly _activities = new BehaviorSubject<Activity[] | null>(null);
  readonly activities$ = this._activities.asObservable();

  private readonly _activityRequirements = new BehaviorSubject<ActivityRequirement[] | null>(null);
  readonly activityRequirements$ = this._activityRequirements.asObservable();

  private readonly _submitProofThankYouMessage = new BehaviorSubject<string | null>(null);
  readonly submitProofThankYouMessage$ = this._submitProofThankYouMessage.asObservable();

  constructor(
    private activityRest: ActivityRestService,
    private uploadRest: UploadRestService,
    private isLoadingService: IsLoadingService,
    private dialog: DialogNgbService
  ) {}

  get activities(): Activity[] | null {
    return this._activities.getValue();
  }

  get activityRequirements(): ActivityRequirement[] | null {
    return this._activityRequirements.getValue();
  }

  getActivities(): void {
    if (this.activities) return;
    this.isLoadingService.add(
      this.activityRest.getActivities().subscribe((activities) => {
        this._activities.next(
          activities.map((a: any) => ({
            ...a,
            promotionStartDate: a.promotionStartDate ? new Date(a.promotionStartDate) : null,
            promotionEndDate: a.promotionEndDate ? new Date(a.promotionEndDate) : null,
          }))
        );
      })
    );
  }

  submitProof(submitProof: SubmitProof): Promise<boolean> {
    return new Promise((resolve) => {
      this.isLoadingService.add(
        this.activityRest.submitProof(submitProof).subscribe(
          () => resolve(true),
          () => resolve(false)
        )
      );
    });
  }

  async getPresignedUrlFile(
    sprintActivityId: number,
    file: File
  ): Promise<{ fileUrl: string; url: string; fileName: string } | null> {
    return new Promise((resolve) => {
      this.isLoadingService.add(
        this.activityRest
          .getPresignedUrlFile({ sprintActivityId, fileName: file.name, fileContentType: file.type })
          .subscribe({
            next: (presignedUrl) => {
              resolve(presignedUrl);
            },

            error: () => {
              resolve(null);
            },
          })
      );
    });
  }

  async uploadFile(presignedUrl: { fileUrl: string; url: string; fileName: string }, file: File): Promise<boolean> {
    return new Promise((resolve) => {
      this.isLoadingService.add(
        this.uploadRest.uploadFileToS3(presignedUrl.url, file, file.type, true).subscribe({
          next: () => {
            resolve(true);
          },
          error: () => {
            resolve(false);
          },
        })
      );
    });
  }

  setActivityRequirements(activityRequirements: ActivityRequirement[]): void {
    this._activityRequirements.next(activityRequirements);
  }

  setSubmitProofThankYouMessage(message: string): void {
    this._submitProofThankYouMessage.next(message);
  }

  resolveSubmission(submissionAnswer: SubmissionAnswer): void {
    this.isLoadingService.add(
      this.activityRest.resolveSubmission(submissionAnswer).subscribe(() => {
        if (submissionAnswer.action === 'approve') {
          this.dialog.openNotification([`Submission approved successfully`], '');
        } else {
          this.dialog.openNotification([`Submission rejected successfully`], '');
        }
      })
    );
  }
}
